import { ArrowDownToLine, CheckCircle2, Clock3, Copy, ShieldCheck, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { isDownloadable, type ReleaseDownload } from "@studycoach/release-manifest";
import { readableBytes } from "../data/release-info";

interface DownloadPanelProps {
  readonly download: ReleaseDownload;
  readonly recommended: boolean;
}

export function DownloadPanel({ download, recommended }: DownloadPanelProps) {
  const [copied, setCopied] = useState(false);
  const downloadable = isDownloadable(download);
  const size = readableBytes(download.size_bytes);

  async function copyHash() {
    if (!download.sha256) return;
    await navigator.clipboard.writeText(download.sha256);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  }

  return (
    <article id="download-panel" className={`download-panel${recommended ? " is-recommended" : ""}`}>
      <div className="download-panel__top">
        <div>
          <span className="platform-name">{download.os === "macos" ? "macOS" : "Windows"}</span>
          <h2>{download.label}</h2>
        </div>
        <div className="download-badges">
          {download.availability === "beta" ? <span className="preview-badge">预览版</span> : null}
          {recommended ? <span className="recommendation"><CheckCircle2 size={14} /> 当前设备</span> : null}
        </div>
      </div>
      <div className="requirement-list">
        {download.system_requirements.map((requirement) => <span key={requirement}>{requirement}</span>)}
      </div>
      <div className="artifact-facts">
        <span>{download.filename}</span>
        {size ? <span>{size}</span> : null}
      </div>
      {download.availability === "beta" ? (
        <div className="preview-notice"><TriangleAlert size={16} /><span>{download.status_note}</span></div>
      ) : null}
      {download.sha256 ? (
        <div className="checksum-block">
          <span><ShieldCheck size={14} /> SHA-256</span>
          <code title={download.sha256}>{download.sha256}</code>
          <button
            type="button"
            onClick={() => void copyHash()}
            aria-label={copied ? "SHA-256 校验值已复制" : "复制 SHA-256 校验值"}
          >
            {copied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
            {copied ? "已复制" : "复制"}
          </button>
          <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {copied ? "SHA-256 校验值已复制" : ""}
          </span>
        </div>
      ) : null}
      {downloadable ? (
        <a className="download-action" href={download.url} download>
          <ArrowDownToLine size={19} />
          下载 {download.label}
        </a>
      ) : (
        <div className="download-status" role="status">
          <Clock3 size={18} aria-hidden="true" />
          <span><strong>暂未开放公开下载</strong>{download.status_note}</span>
        </div>
      )}
    </article>
  );
}
